import { useState, useEffect, useRef } from 'react';
import { Tag } from '@/components/ui/tag';
import { Input } from '@/components/ui/input';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TagInputProps {
  tags: Array<{ id: string; name: string }>;
  availableTags: Array<{ id: string; name: string }>;
  onAddTag: (tagId: string) => void;
  onRemoveTag: (tagId: string) => void;
  onCreateTag: (name: string) => void;
  className?: string;
}

export function TagInput({
  tags,
  availableTags,
  onAddTag,
  onRemoveTag,
  onCreateTag,
  className,
}: TagInputProps) {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on input
  const suggestions = availableTags
    .filter((tag) => {
      const tagIds = tags.map((t) => t.id);
      return (
        !tagIds.includes(tag.id) &&
        tag.name.toLowerCase().includes(input.toLowerCase())
      );
    })
    .slice(0, 5);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddTag = (tagId?: string, tagName?: string) => {
    if (tagId) {
      onAddTag(tagId);
      setInput('');
      setShowSuggestions(false);
    } else if (tagName && tagName.trim()) {
      onCreateTag(tagName.trim());
      setInput('');
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const firstSuggestion = suggestions[0];
      if (firstSuggestion) {
        handleAddTag(firstSuggestion.id);
      } else if (input.trim()) {
        handleAddTag(undefined, input);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <Tag
            key={tag.id}
            name={tag.name}
            onRemove={() => onRemoveTag(tag.id)}
          />
        ))}
      </div>

      <div className="relative">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            type="text"
            placeholder="Add tag..."
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            className="flex-1"
          />
          <button
            onClick={() => handleAddTag(undefined, input)}
            disabled={!input.trim()}
            className="inline-flex items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {showSuggestions && (input || suggestions.length > 0) && (
          <div
            ref={suggestionsRef}
            className="absolute z-10 mt-1 w-full rounded-md border bg-popover p-1 shadow-md"
          >
            {suggestions.length > 0 ? (
              suggestions.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleAddTag(tag.id)}
                  className="w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                >
                  {tag.name}
                </button>
              ))
            ) : input.trim() ? (
              <button
                onClick={() => handleAddTag(undefined, input)}
                className="w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
              >
                Create "{input}"
              </button>
            ) : (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                No tags found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
